import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CreditCard, Smartphone, Building2, Wallet, Check } from 'lucide-react';
import { useApp } from '../context/useApp';
import { subscriptionService } from '../services/firebaseService';
import toast from 'react-hot-toast';

const Payment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSubscription, user, walletBalance, updateWalletBalance } = useApp();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  const selectedPlan = location.state?.plan;

  const paymentMethods = [
    {
      id: 'credit',
      name: 'Credit Card',
      icon: <CreditCard className="w-5 h-5" />,
      description: 'Pay with your credit card'
    },
    {
      id: 'debit',
      name: 'Debit Card',
      icon: <CreditCard className="w-5 h-5" />,
      description: 'Pay with your debit card'
    },
    {
      id: 'upi',
      name: 'UPI',
      icon: <Smartphone className="w-5 h-5" />,
      description: 'Pay using UPI'
    },
    {
      id: 'wallet',
      name: 'Wallet',
      icon: <Wallet className="w-5 h-5" />,
      description: 'Pay using your wallet balance',
      balance: walletBalance
    },
    {
      id: 'netbanking',
      name: 'Net Banking',
      icon: <Building2 className="w-5 h-5" />,
      description: 'Pay using net banking'
    }
  ];

  const handleBack = () => {
    navigate('/plans');
  };

  const handlePaymentMethodSelect = (methodId) => {
    setSelectedPaymentMethod(methodId);
  };

  const handlePayment = async () => {
    if (!selectedPaymentMethod) {
      toast.error('Please select a payment method');
      return;
    }

    setIsProcessing(true);

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Calculate amount
      const planPrice = parseInt(selectedPlan.price.replace('₹', '').replace(',', ''));
      
      // Upgrade subscription using the service
      const result = await subscriptionService.upgradeSubscription(
        selectedPlan.id,
        selectedPaymentMethod,
        planPrice
      );

      if (result.success) {
        // If wallet payment, deduct from balance
        if (selectedPaymentMethod === 'wallet') {
          const newBalance = walletBalance - planPrice;
          updateWalletBalance(newBalance);
        }

        // Reload subscription data
        const subscriptionResult = await subscriptionService.getCurrentUserSubscription();
        if (subscriptionResult.success && subscriptionResult.data) {
          setSubscription(subscriptionResult.data);
        }

        // Check if there's a pending lawyer view to redirect to
        const pendingLawyerView = localStorage.getItem('pendingLawyerView');
        
        if (pendingLawyerView) {
          try {
            const { lawyerId, lawyer } = JSON.parse(pendingLawyerView);
            localStorage.removeItem('pendingLawyerView'); // Clear the pending view
            // Show success message and navigate to lawyer detail
            toast.success(`Payment successful! Your ${selectedPlan.name} is now active.`);
            navigate(`/lawyer-detail/${lawyerId}`, { state: { lawyer } });
          } catch (error) {
            // If there's an error parsing, just go to plans page
            toast.success(`Payment successful! Your ${selectedPlan.name} is now active.`);
            navigate('/plans');
          }
        } else {
          // Show success message and navigate back to plans page
          toast.success(`Payment successful! Your ${selectedPlan.name} is now active.`);
          navigate('/plans');
        }
      } else {
        toast.error('Payment failed. Please try again.');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const calculateFees = () => {
    const planPrice = parseInt(selectedPlan.price.replace('₹', '').replace(',', ''));
    const gst = Math.round(planPrice * 0.18); // 18% GST
    const serviceFee = Math.round(planPrice * 0.05); // 5% service fee
    const total = planPrice + gst + serviceFee;
    
    return { planPrice, gst, serviceFee, total };
  };

  const fees = calculateFees();

  if (!selectedPlan) {
    navigate('/plans');
    return null;
  }
  
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="px-4 pt-6 pb-4">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={handleBack}
            className="flex items-center justify-center p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Payment</h1>
            <p className="text-sm text-gray-500">Complete your subscription to {selectedPlan.name}</p>
          </div>
        </div>
      </div>

      {/* Payment Content */}
      <div className="px-4 pb-20">
        {/* Selected Plan Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-purple-50 rounded-xl p-4 mb-6"
        >
          <h3 className="font-semibold text-purple-900 mb-2">Selected Plan</h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-bold text-purple-900">{selectedPlan.name}</p>
              <p className="text-sm text-purple-700">{selectedPlan.description}</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-purple-900">{selectedPlan.price}</p>
              <p className="text-sm text-purple-700">/month</p>
            </div>
          </div>
        </motion.div>

        {/* Payment Methods */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Payment Method</h3>
          <div className="space-y-3">
            {paymentMethods.map((method) => (
              <button
                key={method.id}
                onClick={() => handlePaymentMethodSelect(method.id)}
                className={`w-full p-4 rounded-xl border-2 transition-all duration-200 ${
                  selectedPaymentMethod === method.id
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      selectedPaymentMethod === method.id ? 'bg-purple-100' : 'bg-gray-100'
                    }`}>
                      <div className={selectedPaymentMethod === method.id ? 'text-purple-600' : 'text-gray-600'}>
                        {method.icon}
                      </div>
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-900">{method.name}</p>
                      <p className="text-sm text-gray-500">{method.description}</p>
                      {method.balance !== undefined && (
                        <p className="text-sm text-green-600 font-medium">
                          Balance: ₹{method.balance.toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                  {selectedPaymentMethod === method.id && (
                    <Check className="w-5 h-5 text-purple-600" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Payment Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gray-50 rounded-xl p-4 mb-6"
        >
          <h3 className="font-semibold text-gray-900 mb-3">Payment Summary</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Plan Fee</span>
              <span className="font-medium">₹{fees.planPrice}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">GST & Service Fee</span>
              <span className="font-medium">₹{fees.gst + fees.serviceFee}</span>
            </div>
            <div className="border-t border-gray-300 pt-2 flex justify-between">
              <span className="font-semibold text-gray-900">Total</span>
              <span className="font-bold text-lg text-purple-600">₹{fees.total}</span>
            </div>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex gap-3"
        >
          <button
            onClick={handleBack}
            className="flex-1 py-3 px-4 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors"
          >
            Back
          </button>
          <button
            onClick={handlePayment}
            disabled={!selectedPaymentMethod || isProcessing}
            className={`flex-1 py-3 px-4 rounded-xl font-medium transition-colors ${
              !selectedPaymentMethod || isProcessing
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
          >
            {isProcessing ? 'Processing...' : 'Pay & Purchase'}
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default Payment;
